import React, { useState, useEffect } from 'react';
import { requestPermission } from '../services/NotificationService';

const STORAGE_KEY = 'wt-notif-prompt-seen';

export default function NotificationPermissionDialog({ isDark }) {
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!('Notification' in window)) return;
    if (Notification.permission !== 'default') return;
    if (sessionStorage.getItem(STORAGE_KEY)) return;
    // Show after 3s
    const t = setTimeout(() => setVisible(true), 3000);
    return () => clearTimeout(t);
  }, []);

  const close = () => {
    setClosing(true);
    sessionStorage.setItem(STORAGE_KEY, '1');
    setTimeout(() => { setVisible(false); setClosing(false); }, 350);
  };

  const allow = () => {
    if (!('Notification' in window)) { close(); return; }
    setLoading(true);
    // Must be called synchronously from click — no async before this line
    const prom = Notification.requestPermission();
    Promise.resolve(prom).then((result) => {
      if (result === 'granted') {
        import('../services/NotificationService').then(({ subscribePush }) => subscribePush());
      }
      setLoading(false);
      close();
    }).catch(() => { setLoading(false); close(); });
  };

  if (!visible) return null;

  const bg     = isDark ? '#0d1b2a'   : '#ffffff';
  const textC  = isDark ? '#e2e8f0'   : '#0f172a';
  const muted  = isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)';
  const border = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';

  const benefits = [
    { icon: <OrderIcon />,   color: '#f59e0b', text: 'تحديثات حالة طلباتك فوراً' },
    { icon: <ChatIcon />,    color: '#0ABAB5', text: 'ردود الدعم الفني على محادثاتك' },
    { icon: <TicketIcon />,  color: '#3b82f6', text: 'ردود فريق الدعم على تذاكرك' },
    { icon: <PromoIcon />,   color: '#a855f7', text: 'عروض وكوبونات حصرية لك' },
  ];

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={close}
        style={{
          position: 'fixed', inset: 0, zIndex: 99990,
          background: 'rgba(0,0,0,0.45)',
          backdropFilter: 'blur(4px)',
          animation: closing ? 'npd-fade-out 0.35s ease forwards' : 'npd-fade-in 0.3s ease forwards',
        }}
      />

      {/* Dialog */}
      <div style={{
        position: 'fixed', zIndex: 99991,
        bottom: 24, left: '50%',
        transform: 'translateX(-50%)',
        width: 'min(340px, calc(100vw - 32px))',
        background: bg,
        borderRadius: '1.25rem',
        border: `1px solid ${border}`,
        boxShadow: isDark
          ? '0 24px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(10,186,181,0.1)'
          : '0 24px 80px rgba(0,0,0,0.15)',
        padding: '1.5rem',
        fontFamily: "'Cairo', sans-serif",
        direction: 'rtl',
        animation: closing ? 'npd-out 0.35s cubic-bezier(0.4,0,1,1) forwards' : 'npd-in 0.4s cubic-bezier(0.16,1,0.3,1) forwards',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
          <div style={{
            width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
            background: 'rgba(10,186,181,0.12)',
            border: '1.5px solid rgba(10,186,181,0.35)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="#0ABAB5" strokeWidth="1.8"
              strokeLinecap="round" width="22" height="22">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
              <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
              <circle cx="18" cy="4" r="3" fill="#ef4444" stroke="none"/>
            </svg>
          </div>
          <div>
            <p style={{ margin: 0, fontWeight: 900, fontSize: '0.95rem', color: textC }}>
              فعّل إشعارات وترلي
            </p>
            <p style={{ margin: 0, fontSize: '0.7rem', color: muted }}>
              ابقَ على اطلاع دائم بكل تحديث
            </p>
          </div>
          <button
            onClick={close}
            style={{
              marginRight: 'auto', background: 'none', border: 'none',
              cursor: 'pointer', padding: 4, color: muted, lineHeight: 1,
            }}
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Benefits */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem', marginBottom: '1.25rem' }}>
          {benefits.map((b, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <div style={{
                width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                background: `${b.color}15`,
                border: `1px solid ${b.color}35`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {React.cloneElement(b.icon, { color: b.color, size: 14 })}
              </div>
              <span style={{ fontSize: '0.75rem', color: textC, fontWeight: 600 }}>{b.text}</span>
              <svg style={{ marginRight: 'auto', flexShrink: 0 }} viewBox="0 0 24 24" width="14" height="14"
                fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={allow}
            disabled={loading}
            style={{
              flex: 1, padding: '0.7rem',
              background: 'linear-gradient(135deg, #0ABAB5, #0891b2)',
              border: 'none', borderRadius: '0.75rem',
              color: '#fff', fontWeight: 800, fontSize: '0.82rem',
              fontFamily: "'Cairo', sans-serif",
              cursor: loading ? 'wait' : 'pointer',
              opacity: loading ? 0.8 : 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem',
              transition: 'opacity 0.2s',
            }}
          >
            {loading ? (
              <span style={{
                width: 14, height: 14, borderRadius: '50%',
                border: '2px solid rgba(255,255,255,0.3)',
                borderTopColor: '#fff',
                animation: 'npd-spin 0.7s linear infinite',
                display: 'inline-block',
              }}/>
            ) : (
              <svg viewBox="0 0 24 24" width="15" height="15" fill="none"
                stroke="#fff" strokeWidth="2" strokeLinecap="round">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
              </svg>
            )}
            السماح بالإشعارات
          </button>
          <button
            onClick={close}
            style={{
              padding: '0.7rem 0.9rem',
              background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
              border: `1px solid ${border}`,
              borderRadius: '0.75rem',
              color: muted, fontWeight: 700, fontSize: '0.78rem',
              fontFamily: "'Cairo', sans-serif",
              cursor: 'pointer',
            }}
          >
            لاحقاً
          </button>
        </div>

        <style>{`
          @keyframes npd-in      { from{opacity:0;transform:translateX(-50%) translateY(24px) scale(0.96)} to{opacity:1;transform:translateX(-50%) translateY(0) scale(1)} }
          @keyframes npd-out     { from{opacity:1;transform:translateX(-50%) translateY(0) scale(1)} to{opacity:0;transform:translateX(-50%) translateY(20px) scale(0.96)} }
          @keyframes npd-fade-in { from{opacity:0} to{opacity:1} }
          @keyframes npd-fade-out{ from{opacity:1} to{opacity:0} }
          @keyframes npd-spin    { to{transform:rotate(360deg)} }
        `}</style>
      </div>
    </>
  );
}

function ChatIcon({ color, size = 16 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none"
      stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>
  );
}
function OrderIcon({ color, size = 16 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none"
      stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V10"/>
      <path d="M23 3H1v7h22V3z"/><line x1="12" y1="3" x2="12" y2="21"/>
    </svg>
  );
}
function TicketIcon({ color, size = 16 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none"
      stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v2z"/>
      <line x1="9" y1="12" x2="15" y2="12"/>
    </svg>
  );
}
function PromoIcon({ color, size = 16 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none"
      stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 12 20 22 4 22 4 12"/>
      <rect x="2" y="7" width="20" height="5" rx="1"/>
      <line x1="12" y1="22" x2="12" y2="7"/>
      <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/>
      <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/>
    </svg>
  );
}
